/**
 * buildScene: design document + resolved library + findings + view -> Scene.
 *
 * A pure function of its inputs. The design document is the truth and the scene
 * is recomputed from it; nothing is ever read back out of a scene into a design
 * (design §8, D13).
 *
 * Three rules the builder holds to:
 *
 * - **Drawn geometry carries no physics.** Edge paths are the shortest sensible
 *   polyline between ports. They are never lengthened towards the hydraulic
 *   length (D18); that length travels as a number and a label instead.
 * - **Placement first, editor coordinates as fallback.** Phase 2 populating
 *   `Node.placement` changes which branch `resolvePosition` takes and nothing
 *   else (requirement 11.5).
 * - **Collapse is a view concern.** A collapsed block hides its members and
 *   internal edges from the scene; the document is untouched (requirement 8.6).
 *
 * Incremental rebuild (requirement 14.4): `createSceneBuilder` memoises each
 * node, edge and block on the identity of its inputs. The editor's reducer
 * replaces only the objects an edit touches, so moving one node recomputes that
 * node and the edges attached to it — cost scales with the edit, not the
 * schematic. `buildScene` is the one-shot form for callers without history.
 */
import type {
  Anchor,
  DesignDocument,
  Direction,
  EdgeDocument,
  Finding,
  GroupInstanceDocument,
  NodeDocument,
  Severity,
  SymbolSpec,
  TemplateDocument,
} from "../design/document";
import { formatLength } from "./format";
import type { DisplayUnit } from "../workspace/units";
import {
  DEFAULT_ROUTE_OPTIONS,
  labelPoint,
  obstaclesIn,
  routeArea,
  routeOrthogonal,
  routeThrough,
  type Rect,
} from "./route";
import { isClampOn, partition } from "./consumable";
import { buildNote } from "./notes";
import {
  ALL_ANNOTATIONS,
  lineShown,
  DEFAULT_VIEW,
  type Bounds,
  type DefinitionEntry,
  type Point,
  type Scene,
  type SceneBlock,
  type SceneEdge,
  type SceneEdgeEnd,
  type SceneFinding,
  type SceneLibrary,
  type SceneNode,
  type SceneNote,
  type ScenePort,
  type SceneTubeRun,
  type SceneView,
  type Size,
} from "./types";

/** Drawn when a node's template cannot be resolved, so the node stays visible. */
const FALLBACK_SYMBOL: Required<SymbolSpec> = {
  glyph: "unknown",
  width: 48,
  height: 32,
  badge: "",
};

/** A block definition that does not size its symbol gets something block-sized. */
const DEFAULT_BLOCK_SIZE: Size = { width: 96, height: 64 };

/** How far an edge runs straight out of a port before turning towards the other. */
export const EDGE_STUB = DEFAULT_ROUTE_OPTIONS.stub;

/** Margin between an expanded block's frame and its members. */
export const FRAME_PADDING = 16;

/** Height a node's label occupies below its symbol, as the renderer draws it. */
export const NODE_LABEL_ALLOWANCE = 12;

const NO_FINDINGS: readonly SceneFinding[] = Object.freeze([]);

const SEVERITY_RANK: Record<Severity, number> = { info: 1, warning: 2, error: 3 };

// Clockwise with y pointing down, matching SVG's positive rotation.
const COMPASS: readonly Direction[] = ["east", "south", "west", "north"];


// --- Memoisation ------------------------------------------------------------

interface Entry<T> {
  inputs: readonly unknown[];
  value: T;
}

/** Inputs match by identity; arrays match element-wise by identity. */
function sameInput(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) {
    return true;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, index) => Object.is(item, b[index]));
  }
  return false;
}

function sameInputs(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((item, index) => sameInput(item, b[index]));
}

/**
 * A per-element cache that keeps only what the latest build visited, so a
 * deleted element's entry does not outlive it.
 */
class ElementMemo<T> {
  private previous = new Map<string, Entry<T>>();
  private current = new Map<string, Entry<T>>();
  computed = 0;

  get(key: string, inputs: readonly unknown[], compute: () => T): T {
    const entry = this.previous.get(key);
    let value: T;
    if (entry !== undefined && sameInputs(entry.inputs, inputs)) {
      value = entry.value;
    } else {
      value = compute();
      this.computed += 1;
    }
    this.current.set(key, { inputs, value });
    return value;
  }

  begin(): void {
    this.computed = 0;
  }

  finish(): void {
    this.previous = this.current;
    this.current = new Map();
  }
}

// --- Geometry helpers -------------------------------------------------------

/** Rounds away floating-point noise from rotation, so equal points compare equal. */
function tidy(value: number): number {
  const rounded = Math.round(value * 1e6) / 1e6;
  return Object.is(rounded, -0) ? 0 : rounded;
}

function rotate(offset: Point, rotation: number): Point {
  if (rotation === 0) {
    return offset;
  }
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  return { x: offset.x * cos - offset.y * sin, y: offset.x * sin + offset.y * cos };
}

/**
 * Port directions follow the symbol: mirrored left-right first, then turned to
 * the nearest quarter.
 */
function orientDirection(direction: Direction, rotation: number, mirrored: boolean): Direction {
  const quarterTurns = Math.round(rotation / (Math.PI / 2));
  const facing = mirrored && (direction === "east" || direction === "west")
    ? (direction === "east" ? "west" : "east")
    : direction;
  const index = COMPASS.indexOf(facing);
  return COMPASS[(((index + quarterTurns) % 4) + 4) % 4]!;
}

/**
 * A point on a box from a normalised anchor: mirrored left-right in the box's
 * own frame, then rotated about its centre. The renderer draws the glyph with
 * the same two transforms in the same order, so ports sit on the drawing.
 */
function anchorPoint(
  centre: Point,
  size: Size,
  anchor: Anchor,
  rotation: number,
  mirrored: boolean,
): Point {
  const local = (anchor.x - 0.5) * size.width;
  const offset = rotate(
    { x: mirrored ? -local : local, y: (anchor.y - 0.5) * size.height },
    rotation,
  );
  return { x: tidy(centre.x + offset.x), y: tidy(centre.y + offset.y) };
}

function boxCorners(centre: Point, size: Size, rotation: number): Point[] {
  const halfW = size.width / 2;
  const halfH = size.height / 2;
  return [
    { x: -halfW, y: -halfH },
    { x: halfW, y: -halfH },
    { x: halfW, y: halfH },
    { x: -halfW, y: halfH },
  ].map((corner) => {
    const turned = rotate(corner, rotation);
    return { x: centre.x + turned.x, y: centre.y + turned.y };
  });
}

class BoundsAccumulator {
  private minX = Infinity;
  private minY = Infinity;
  private maxX = -Infinity;
  private maxY = -Infinity;

  add(point: Point): void {
    this.minX = Math.min(this.minX, point.x);
    this.minY = Math.min(this.minY, point.y);
    this.maxX = Math.max(this.maxX, point.x);
    this.maxY = Math.max(this.maxY, point.y);
  }

  get empty(): boolean {
    return this.minX === Infinity;
  }

  result(): Bounds {
    if (this.empty) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    }
    return {
      minX: tidy(this.minX),
      minY: tidy(this.minY),
      maxX: tidy(this.maxX),
      maxY: tidy(this.maxY),
    };
  }
}

// --- Findings ---------------------------------------------------------------

function normaliseSeverity(severity: string): Severity {
  const lowered = severity.toLowerCase();
  if (lowered === "error" || lowered === "warning" || lowered === "info") {
    return lowered;
  }
  // An unrecognised severity is shown rather than dropped, but not as an error.
  return "warning";
}

function worstOf(findings: readonly SceneFinding[]): Severity | null {
  let worst: Severity | null = null;
  for (const finding of findings) {
    if (worst === null || SEVERITY_RANK[finding.severity] > SEVERITY_RANK[worst]) {
      worst = finding.severity;
    }
  }
  return worst;
}

const NOTHING_WASHED: ReadonlySet<string> = new Set();

function topSegment(path: string): string {
  const dot = path.indexOf(".");
  return dot === -1 ? path : path.slice(0, dot);
}

/** Findings indexed by the top-level element their path names. */
interface FindingIndex {
  byElement: Map<string, SceneFinding[]>;
  unattached: SceneFinding[];
}

// --- Position ---------------------------------------------------------------

interface ResolvedPosition {
  centre: Point;
  rotation: number;
  mirrored: boolean;
  source: SceneNode["positionSource"];
}

/**
 * The only place a node's position is decided. Placement is Phase 2's physical
 * layout in metre, so it is scaled into scene units; schematic coordinates are
 * already scene units.
 */
function resolvePosition(node: NodeDocument, placementScale: number): ResolvedPosition {
  if (node.placement) {
    return {
      centre: {
        x: tidy(node.placement.x * placementScale),
        y: tidy(node.placement.y * placementScale),
      },
      rotation: node.placement.rotation ?? 0,
      // Placement is physical, and a physical part is not mirrored by drawing
      // it differently; if Phase 2 needs a mirrored part it is a placement
      // concern, so the schematic's mirror does not carry over.
      mirrored: false,
      source: "placement",
    };
  }
  if (node.schematic_position) {
    return {
      centre: { x: node.schematic_position.x, y: node.schematic_position.y },
      rotation: node.schematic_position.rotation ?? 0,
      mirrored: node.schematic_position.mirrored ?? false,
      source: "schematic",
    };
  }
  return { centre: { x: 0, y: 0 }, rotation: 0, mirrored: false, source: "unplaced" };
}

// --- Element builders -------------------------------------------------------

function symbolSize(symbol: SymbolSpec, fallback: Size): Size {
  return { width: symbol.width ?? fallback.width, height: symbol.height ?? fallback.height };
}

function buildNode(
  node: NodeDocument,
  template: TemplateDocument | undefined,
  findings: readonly SceneFinding[],
  placementScale: number,
): SceneNode {
  const symbol = template?.symbol ?? FALLBACK_SYMBOL;
  const size = symbolSize(symbol, FALLBACK_SYMBOL);
  const { centre, rotation, mirrored, source } = resolvePosition(node, placementScale);
  const declared = new Map((template?.ports ?? []).map((port) => [port.local_id, port]));
  const documentPorts = node.ports ?? [];

  const ports: ScenePort[] = documentPorts.map((port, index) => {
    const ref = `${node.local_id}.${port.local_id}`;
    // A port the template does not declare (template missing, or a port added
    // outside it) is spaced down the east side rather than dropped.
    const anchor = declared.get(port.local_id)?.anchor ?? {
      x: 1,
      y: (index + 1) / (documentPorts.length + 1),
      direction: "east" as const,
    };
    const portFindings = findings.filter((finding) => finding.path === ref);
    return {
      ref,
      name: port.name || declared.get(port.local_id)?.name || port.local_id,
      kind: port.kind ?? "fluidic",
      isExternal: port.is_external ?? false,
      deadEnd: port.dead_end ?? false,
      position: anchorPoint(centre, size, anchor, rotation, mirrored),
      direction: orientDirection(anchor.direction ?? "east", rotation, mirrored),
      findings: portFindings.length ? portFindings : NO_FINDINGS,
      worstSeverity: worstOf(portFindings),
    };
  });

  return {
    ref: node.local_id,
    role: node.role,
    // A junction is a point on a rail (D32): unlabelled unless the designer
    // gave it a label, where a component shows its identifier.
    label: node.label || (symbol.glyph === "junction" ? "" : node.local_id),
    glyph: symbol.glyph,
    badge: symbol.badge ?? "",
    centre,
    size,
    rotation,
    mirrored,
    positionSource: source,
    templateVersionId: node.template_version_id ?? null,
    templateMissing: node.template_version_id != null && template === undefined,
    blockInstance: node.group_instance ?? null,
    labelSide: node.schematic_position?.label_side ?? "below",
    ports,
    findings,
    worstSeverity: worstOf(findings),
  };
}

/** The frame around a set of nodes: their rotated boxes' extent. */
function frameAround(members: readonly SceneNode[]): { centre: Point; size: Size } {
  const bounds = new BoundsAccumulator();
  for (const member of members) {
    for (const corner of boxCorners(member.centre, member.size, member.rotation)) {
      bounds.add(corner);
    }
  }
  const box = bounds.result();
  return {
    centre: { x: tidy((box.minX + box.maxX) / 2), y: tidy((box.minY + box.maxY) / 2) },
    size: { width: box.maxX - box.minX, height: box.maxY - box.minY },
  };
}

/**
 * Where a collapsed block goes when the designer has not put it anywhere: the
 * centre that makes its lines to the outside shortest. Lines are routed at
 * right angles, so their length is the sum of the horizontal and vertical
 * runs, and each axis can be minimised on its own:
 *
 * - Vertically, the median of the heights that would put each port level
 *   with the far end of its line. With one line in and one line out from the
 *   same rail, both heights are the rail's, and the block sits on it.
 * - Horizontally, within the range that keeps every port facing what it
 *   connects to (a west port's line leaves to the west) with a grid step of
 *   clearance, the point that minimises the runs; where several do equally
 *   well, the one nearest the middle of the range, snapped to the grid.
 *
 * Unturned symbols only; a turned one keeps the members' centre.
 */
export function collapsedCentre(
  frameCentre: Point,
  size: Size,
  exposed: readonly { anchor: Anchor; far: Point | undefined }[],
  turned: boolean,
  grid: number,
): Point {
  const wired = exposed.filter((port): port is { anchor: Anchor; far: Point } => port.far !== undefined);
  if (turned || wired.length === 0) return frameCentre;
  const offsetX = (port: { anchor: Anchor }) => (port.anchor.x - 0.5) * size.width;
  const offsetY = (port: { anchor: Anchor }) => (port.anchor.y - 0.5) * size.height;

  const heights = wired.map((port) => port.far.y - offsetY(port)).sort((a, b) => a - b);
  const middle = heights.length >> 1;
  const y = heights.length % 2 === 1 ? heights[middle]! : (heights[middle - 1]! + heights[middle]!) / 2;

  const clearance = Math.max(grid, 16) * 2;
  const west = wired.filter((port) => port.anchor.x < 0.5);
  const east = wired.filter((port) => port.anchor.x >= 0.5);
  const low = west.length ? Math.max(...west.map((port) => port.far.x + clearance - offsetX(port))) : -Infinity;
  const high = east.length ? Math.min(...east.map((port) => port.far.x - clearance - offsetX(port))) : Infinity;
  const run = (x: number) => wired.reduce((sum, port) => sum + Math.abs(port.far.x - (x + offsetX(port))), 0);
  let x: number;
  if (low > high) {
    // Nowhere keeps every port facing its line: between the two sides, then.
    x = Number.isFinite(low) && Number.isFinite(high) ? (low + high) / 2 : frameCentre.x;
  } else {
    const inRange = (value: number) => Math.min(high, Math.max(low, value));
    const mid = Number.isFinite(low) && Number.isFinite(high) ? (low + high) / 2 : inRange(frameCentre.x);
    const candidates = [mid, ...wired.map((port) => inRange(port.far.x - offsetX(port)))].filter(Number.isFinite);
    x = candidates.reduce((best, candidate) => {
      const difference = run(candidate) - run(best);
      if (difference < -1e-6) return candidate;
      return Math.abs(difference) <= 1e-6 && Math.abs(candidate - mid) < Math.abs(best - mid) ? candidate : best;
    }, mid);
  }
  const snapped = grid > 0 ? Math.round(x / grid) * grid : x;
  return { x: tidy(snapped), y: tidy(y) };
}

function buildBlock(
  instance: GroupInstanceDocument,
  definition: DefinitionEntry | undefined,
  collapsed: boolean,
  members: readonly SceneNode[],
  ownFindings: readonly SceneFinding[],
  memberFindings: readonly SceneFinding[],
  /** Member port ref -> the far end of its line to the outside, for placing it collapsed. */
  outside: ReadonlyMap<string, Point> = new Map(),
  grid = 0,
): SceneBlock {
  const frame = frameAround(members);
  const findings = collapsed ? [...ownFindings, ...memberFindings] : [...ownFindings];
  const symbol = definition?.content.symbol ?? { glyph: "block" };
  const orientation = {
    rotation: instance.schematic_orientation?.rotation ?? 0,
    mirrored: instance.schematic_orientation?.mirrored ?? false,
  };
  const base = {
    ref: instance.local_id,
    name: definition?.name ?? instance.local_id,
    definitionVersionId: instance.group_definition_version_id,
    definitionResolved: definition !== undefined,
    glyph: symbol.glyph,
    memberRefs: instance.member_local_ids ?? [],
    findings: findings.length ? findings : NO_FINDINGS,
    worstSeverity: worstOf(findings),
    rotation: orientation.rotation,
    mirrored: orientation.mirrored,
  };

  if (!collapsed) {
    return {
      ...base,
      collapsed: false,
      // Extra room below for the members' labels, which sit under their
      // symbols; without it the frame's bottom edge strikes through them.
      centre: { x: frame.centre.x, y: tidy(frame.centre.y + NODE_LABEL_ALLOWANCE / 2) },
      size: {
        width: frame.size.width + 2 * FRAME_PADDING,
        height: frame.size.height + 2 * FRAME_PADDING + NODE_LABEL_ALLOWANCE,
      },
      ports: [],
    };
  }

  // Exposed ports name internal ports in the definition's identifiers
  // (`valve_1.port_a`); the instance's members carry fresh ones (`valve_7`), so
  // each is translated through the recorded origins before it can be matched
  // against the edges that actually reference it.
  const size = symbolSize(symbol, DEFAULT_BLOCK_SIZE);
  const expansion = new Map(
    Object.entries(instance.member_origins ?? {}).map(([member, origin]) => [origin, member]),
  );
  const resolved = (definition?.content.exposed_ports ?? []).flatMap((exposed) => {
    const origin = topSegment(exposed.internal_ref);
    const member = expansion.get(origin);
    // The member it names is gone; the lock check reports that divergence.
    if (member === undefined) return [];
    const ref = member + exposed.internal_ref.slice(origin.length);
    const anchor = exposed.anchor ?? { x: 1, y: 0.5, direction: "east" as const };
    return [{ exposed, ref, anchor, far: outside.get(ref) }];
  });
  const stored = instance.collapsed_position;
  const centre = stored
    ? { x: stored.x, y: stored.y }
    : collapsedCentre(frame.centre, size, resolved, orientation.rotation !== 0 || orientation.mirrored, grid);
  const ports: ScenePort[] = [];
  for (const { exposed, ref, anchor } of resolved) {
    const portFindings = memberFindings.filter((finding) => finding.path === ref);
    ports.push({
      ref,
      name: exposed.name,
      kind: "fluidic",
      isExternal: false,
      deadEnd: false,
      position: anchorPoint(centre, size, anchor, orientation.rotation, orientation.mirrored),
      direction: orientDirection(anchor.direction ?? "east", orientation.rotation, orientation.mirrored),
      findings: portFindings.length ? portFindings : NO_FINDINGS,
      worstSeverity: worstOf(portFindings),
    });
  }

  return { ...base, collapsed: true, centre, size, ports };
}

/** What an edge endpoint resolved to. */
type EndpointSource =
  | { kind: "port"; port: ScenePort }
  | { kind: "exposed"; port: ScenePort; block: string }
  | { kind: "concealed"; block: SceneBlock };

function endpointOf(ref: string, source: EndpointSource): SceneEdgeEnd {
  if (source.kind === "concealed") {
    return { ref, point: source.block.centre, direction: null, concealed: true };
  }
  return {
    ref,
    point: source.port.position,
    direction: source.port.direction,
    concealed: false,
  };
}

function buildEdge(
  edge: EdgeDocument,
  template: TemplateDocument | undefined,
  findings: readonly SceneFinding[],
  from: SceneEdgeEnd,
  to: SceneEdgeEnd,
  blockInstance: string | null,
  serpentineThreshold: number,
  obstacles: readonly Rect[],
  area: Rect,
  lengthUnit: DisplayUnit | null = null,
): SceneEdge {
  // Right-angled, out of each port the way it faces, around every symbol in
  // the way (D23). Never lengthened towards the hydraulic length: a meander
  // would look like a layout and is not one (D18).
  const waypoints = edge.schematic_waypoints ?? [];
  const routed =
    waypoints.length > 0
      ? routeThrough(from, to, waypoints)
      : routeOrthogonal(from, to, obstacles, DEFAULT_ROUTE_OPTIONS, area);
  const path = routed.map((point) => ({
    x: tidy(point.x),
    y: tidy(point.y),
  }));
  const length = edge.hydraulic?.length ?? null;
  const type = edge.type ?? "channel";
  const badge = template?.symbol.badge ?? "";
  const mixing = type === "mixing_channel" || badge === "mixer";
  const air = type === "air_line";
  const serpentine =
    !mixing &&
    !air &&
    (badge === "serpentine" ||
      template?.symbol.glyph === "serpentine" ||
      (length !== null && length > serpentineThreshold));

  return {
    ref: edge.local_id,
    type,
    air,
    from,
    to,
    path,
    hydraulicLength: length,
    lengthLabel: length === null ? null : formatLength(length, lengthUnit),
    labelPosition: labelPoint(path),
    manual: waypoints.length > 0,
    serpentine,
    mixing,
    blockInstance,
    findings,
    worstSeverity: worstOf(findings),
  };
}

// --- The builder ------------------------------------------------------------

export interface BuildStats {
  nodesComputed: number;
  edgesComputed: number;
  blocksComputed: number;
}

export interface SceneBuilder {
  build(
    design: DesignDocument,
    library: SceneLibrary,
    findings?: readonly Finding[],
    view?: SceneView,
  ): Scene;
  /** What the most recent build had to recompute. */
  readonly lastBuild: BuildStats;
}

/** The axis-aligned box a symbol occupies. */
function boxOf(element: SceneNode | SceneBlock, rotation: number): Rect {
  const corners = boxCorners(element.centre, element.size, rotation);
  return {
    minX: tidy(Math.min(...corners.map((c) => c.x))),
    minY: tidy(Math.min(...corners.map((c) => c.y))),
    maxX: tidy(Math.max(...corners.map((c) => c.x))),
    maxY: tidy(Math.max(...corners.map((c) => c.y))),
  };
}

export function createSceneBuilder(): SceneBuilder {
  const nodeMemo = new ElementMemo<SceneNode>();
  const edgeMemo = new ElementMemo<SceneEdge>();
  const blockMemo = new ElementMemo<SceneBlock>();
  // Obstacles keyed on geometry, not on the scene element: a node rebuilt for
  // a new findings badge occupies the same box, and its neighbours' routes
  // must not be recomputed for it.
  const obstacleMemo = new ElementMemo<Rect>();
  // One SceneFinding per API finding object, so unchanged findings keep their
  // identity and do not defeat the element memo.
  const findingCache = new WeakMap<Finding, SceneFinding>();
  let lastBuild: BuildStats = { nodesComputed: 0, edgesComputed: 0, blocksComputed: 0 };
  let last: { inputs: readonly unknown[]; scene: Scene } | null = null;

  function toSceneFinding(finding: Finding): SceneFinding {
    let scene = findingCache.get(finding);
    if (scene === undefined) {
      scene = {
        code: finding.code,
        message: finding.message,
        severity: normaliseSeverity(finding.severity),
        path: finding.path ?? null,
      };
      findingCache.set(finding, scene);
    }
    return scene;
  }

  function indexFindings(findings: readonly Finding[], known: ReadonlySet<string>): FindingIndex {
    const byElement = new Map<string, SceneFinding[]>();
    const unattached: SceneFinding[] = [];
    for (const finding of findings) {
      const scene = toSceneFinding(finding);
      const element = scene.path === null ? null : topSegment(scene.path);
      if (element === null || !known.has(element)) {
        unattached.push(scene);
        continue;
      }
      const list = byElement.get(element);
      if (list === undefined) {
        byElement.set(element, [scene]);
      } else {
        list.push(scene);
      }
    }
    return { byElement, unattached };
  }

  function build(
    design: DesignDocument,
    library: SceneLibrary,
    findings: readonly Finding[] = [],
    view: SceneView = DEFAULT_VIEW,
  ): Scene {
    const inputs = [design, library, findings, view];
    if (last !== null && sameInputs(last.inputs, inputs)) {
      lastBuild = { nodesComputed: 0, edgesComputed: 0, blocksComputed: 0 };
      return last.scene;
    }

    obstacleMemo.begin();
    nodeMemo.begin();
    edgeMemo.begin();
    blockMemo.begin();

    const documentNodes = design.nodes ?? [];
    const documentEdges = design.edges ?? [];
    const instances = (design.group_instances ?? []).filter((instance) => !instance.detached);

    const known = new Set<string>([
      ...documentNodes.map((node) => node.local_id),
      ...documentEdges.map((edge) => edge.local_id),
      ...instances.map((instance) => instance.local_id),
    ]);
    const index = indexFindings(findings, known);
    const findingsFor = (ref: string): readonly SceneFinding[] =>
      index.byElement.get(ref) ?? NO_FINDINGS;

    // Nodes: every node is built, including members of collapsed blocks, because
    // a collapsed block is positioned from its members.
    const sceneNodes = new Map<string, SceneNode>();
    for (const node of documentNodes) {
      // Placed on its tube once the tube is routed, below.
      if (node.mount) continue;
      const template = node.template_version_id
        ? library.templates[node.template_version_id]
        : undefined;
      const nodeFindings = findingsFor(node.local_id);
      sceneNodes.set(
        node.local_id,
        nodeMemo.get(node.local_id, [node, template, nodeFindings, view.placementScale], () =>
          buildNode(node, template, nodeFindings, view.placementScale),
        ),
      );
    }

    // Blocks, and which collapsed block (if any) hides each member.
    const memberOf = new Map<string, string>();
    const annotations = view.annotations ?? ALL_ANNOTATIONS;
    const hiddenBy = new Map<string, SceneBlock>();
    const sceneBlocks: SceneBlock[] = [];
    for (const instance of instances) {
      const members = instance.member_local_ids ?? [];
      const collapsed = view.collapsed.has(instance.local_id);
      const memberNodes = members
        .map((member) => sceneNodes.get(member))
        .filter((node): node is SceneNode => node !== undefined);
      const memberFindings = members.flatMap((member) => findingsFor(member));
      const ownFindings = findingsFor(instance.local_id);
      const definition = library.definitions[instance.group_definition_version_id];
      // For placing it collapsed: where each line from a member to the outside ends.
      const outside = new Map<string, Point>();
      if (collapsed && !instance.collapsed_position) {
        const inside = new Set(members);
        for (const edge of documentEdges) {
          for (const [near, farRef] of [
            [edge.from_port, edge.to_port],
            [edge.to_port, edge.from_port],
          ] as const) {
            if (!inside.has(topSegment(near)) || inside.has(topSegment(farRef))) continue;
            const far = sceneNodes.get(topSegment(farRef))?.ports.find((port) => port.ref === farRef);
            if (far) outside.set(near, far.position);
          }
        }
      }
      const outsideKey = [...outside].map(([ref, point]) => `${ref}@${point.x},${point.y}`).join(" ");
      const block = blockMemo.get(
        instance.local_id,
        [instance, definition, collapsed, memberNodes, ownFindings, memberFindings, outsideKey, view.grid.spacing],
        () =>
          buildBlock(
            instance,
            definition,
            collapsed,
            memberNodes,
            ownFindings,
            memberFindings,
            outside,
            view.grid.spacing,
          ),
      );
      sceneBlocks.push(block);
      for (const member of members) {
        memberOf.set(member, instance.local_id);
        if (collapsed) {
          hiddenBy.set(member, block);
        }
      }
    }

    const resolveEndpoint = (ref: string): EndpointSource | null => {
      const node = sceneNodes.get(topSegment(ref));
      const port = node?.ports.find((candidate) => candidate.ref === ref);
      if (node === undefined || port === undefined) {
        return null;
      }
      const block = hiddenBy.get(node.ref);
      if (block === undefined) {
        return { kind: "port", port };
      }
      const exposed = block.ports.find((candidate) => candidate.ref === ref);
      return exposed !== undefined
        ? { kind: "exposed", port: exposed, block: block.ref }
        : { kind: "concealed", block };
    };

    const blockOfSource = (source: EndpointSource): string | null =>
      source.kind === "port" ? null : source.kind === "exposed" ? source.block : source.block.ref;

    // What connector lines must go around: every drawn symbol. Each rectangle
    // is cached on its scene element, so an unchanged symbol keeps an
    // identical obstacle and the routes near it stay memoised.
    const obstacles: Rect[] = [];
    const obstacleFor = (element: SceneNode | SceneBlock, rotation: number) =>
      obstacleMemo.get(
        element.ref,
        [element.centre.x, element.centre.y, element.size.width, element.size.height, rotation],
        () => boxOf(element, rotation),
      );
    // A part mounted on a tube (D40) sits on it: not something to go round.
    const mountedIds = new Set(documentNodes.filter((node) => node.mount).map((node) => node.local_id));
    for (const node of sceneNodes.values()) {
      if (!hiddenBy.has(node.ref) && !mountedIds.has(node.ref)) obstacles.push(obstacleFor(node, node.rotation));
    }
    for (const block of sceneBlocks) {
      if (block.collapsed) obstacles.push(obstacleFor(block, block.rotation));
    }

    const sceneEdges: SceneEdge[] = [];
    const unresolvedEdges: string[] = [];
    for (const edge of documentEdges) {
      const fromSource = resolveEndpoint(edge.from_port);
      const toSource = resolveEndpoint(edge.to_port);
      if (fromSource === null || toSource === null) {
        unresolvedEdges.push(edge.local_id);
        continue;
      }
      // Internal to a collapsed block: a member edge, or any edge whose two ends
      // are both inside the same collapsed block.
      const fromBlock = blockOfSource(fromSource);
      if (hiddenBy.has(edge.local_id) || (fromBlock !== null && fromBlock === blockOfSource(toSource))) {
        continue;
      }
      // A kind of line switched off in the view is not in the scene at all, so
      // it can be neither seen nor hit (and an export leaves it out too).
      if (!lineShown(annotations, edge.type)) continue;
      const template = edge.template_version_id
        ? library.templates[edge.template_version_id]
        : undefined;
      const edgeFindings = findingsFor(edge.local_id);
      const blockInstance = memberOf.get(edge.local_id) ?? null;
      const from = endpointOf(edge.from_port, fromSource);
      const to = endpointOf(edge.to_port, toSource);
      // The route depends only on the obstacles near it, so moving a component
      // re-routes the edges around it rather than every edge (requirement 14.4).
      const area = routeArea(from, to, obstacles);
      const nearby = obstaclesIn(area, obstacles);
      // Keyed on where the ends are, not on the port objects: a node rebuilt
      // for a new badge keeps its ports in place and its edges need nothing.
      sceneEdges.push(
        edgeMemo.get(
          edge.local_id,
          [
            edge,
            template,
            edgeFindings,
            from.point.x,
            from.point.y,
            from.direction,
            to.point.x,
            to.point.y,
            to.direction,
            blockInstance,
            view.serpentineThreshold,
            nearby,
            view.units?.length,
          ],
          () =>
            buildEdge(
              edge,
              template,
              edgeFindings,
              from,
              to,
              blockInstance,
              view.serpentineThreshold,
              nearby,
              area,
              view.units?.length ?? null,
            ),
        ),
      );
    }

    // Mounted parts, on the drawn line of the tube they sit on (D40): at
    // `along`, or where their hydraulic distance falls, turned to the line.
    // Where the tube is not drawn, the part is drawn where it last stood.
    for (const node of documentNodes) {
      if (!node.mount) continue;
      const host = sceneEdges.find((edge) => edge.ref === node.mount!.edge);
      const template = node.template_version_id ? library.templates[node.template_version_id] : undefined;
      if (!host) {
        const nodeFindings = findingsFor(node.local_id);
        sceneNodes.set(
          node.local_id,
          nodeMemo.get(node.local_id, [node, template, nodeFindings, view.placementScale], () =>
            buildNode(node, template, nodeFindings, view.placementScale),
          ),
        );
        continue;
      }
      const placed = pointAlong(host.path, mountFraction(node.mount, host.hydraulicLength));
      const onLine: NodeDocument = {
        ...node,
        schematic_position: {
          ...(node.schematic_position ?? {}),
          x: placed.point.x,
          y: placed.point.y,
          rotation: placed.rotation,
          mirrored: placed.mirrored,
        },
      };
      const nodeFindings = findingsFor(node.local_id);
      const key = [node, template, nodeFindings, view.placementScale, placed.point.x, placed.point.y, placed.rotation, placed.mirrored];
      sceneNodes.set(
        node.local_id,
        nodeMemo.get(node.local_id, key, () => ({
          ...buildNode(onLine, template, nodeFindings, view.placementScale),
          mountedOn: host.ref,
        })),
      );
    }

    nodeMemo.finish();
    edgeMemo.finish();
    blockMemo.finish();
    obstacleMemo.finish();

    // One side of the external interfaces washed, and with `isolate` the
    // other side left out of the scene altogether: not drawn, not hit, not
    // exported. With no boundary there is no side to show, so nothing is hidden.
    const washed =
      annotations.wash === "none" ? NOTHING_WASHED : partition(design, library.templates)[annotations.wash];
    const isolating = Boolean(annotations.isolate) && washed.size > 0;
    if (isolating) {
      const kept = sceneEdges.filter((edge) => washed.has(edge.ref));
      sceneEdges.length = 0;
      sceneEdges.push(...kept);
      const blocks = sceneBlocks.filter((block) => washed.has(block.ref));
      sceneBlocks.length = 0;
      sceneBlocks.push(...blocks);
    }

    const frames = sceneBlocks.filter((block) => !block.collapsed);
    // Showing only the set: a clamp-on part on it goes, and the tube through
    // it stays, drawn port to port as the part's own symbol draws it. Only
    // where both tubes on it are shown, so a line is never extended to nothing.
    const tubeRuns: SceneTubeRun[] = [];
    if (isolating && annotations.wash === "inside") {
      const shownAt = new Map<string, SceneEdge>();
      for (const edge of sceneEdges) {
        shownAt.set(edge.from.ref, edge);
        shownAt.set(edge.to.ref, edge);
      }
      for (const node of documentNodes) {
        const template = node.template_version_id ? library.templates[node.template_version_id] : undefined;
        if (washed.has(node.local_id) || !isClampOn(node, template)) continue;
        const drawn = sceneNodes.get(node.local_id);
        const through = (drawn?.ports ?? []).filter((port) => port.kind === "fluidic");
        const lines = through.map((port) => shownAt.get(port.ref));
        if (!drawn || hiddenBy.has(drawn.ref) || through.length !== 2 || lines.some((line) => !line)) continue;
        tubeRuns.push({
          ref: drawn.ref,
          from: through[0]!.position,
          to: through[1]!.position,
          air: lines.every((line) => line!.air),
        });
      }
    }

    const visibleNodes = [...sceneNodes.values()]
      .filter((node) => !hiddenBy.has(node.ref) && (!isolating || washed.has(node.ref)))
      .map((node) => {
        // A new object only for a node that is stray, so the memo holds for the rest.
        const frame = frames.find(
          (block) =>
            block.ref !== node.blockInstance &&
            Math.abs(node.centre.x - block.centre.x) < block.size.width / 2 &&
            Math.abs(node.centre.y - block.centre.y) < block.size.height / 2,
        );
        if (frame) return node.strayIn === frame.ref ? node : { ...node, strayIn: frame.ref };
        return node.strayIn === undefined ? node : { ...node, strayIn: undefined };
      });

    // Notes, unless the view hides them: then none at all, so a hidden note
    // can be neither seen nor hit, and an export leaves it out too. An
    // unchanged note keeps its object, as elements do in their memos.
    const drawn = new Set(visibleNodes.map((node) => node.ref));
    const sceneNotes: SceneNote[] = annotations.notes
      ? (design.text_notes ?? []).map((note) => {
          const built = buildNote(note, drawn);
          const previous = last?.scene.notes.find((candidate) => candidate.ref === built.ref);
          return previous && sameNote(previous, built) ? previous : built;
        })
        // Isolating a side keeps the notes on it: those attached to what is shown.
        .filter((note) => !isolating || (note.anchor !== null && washed.has(note.anchor)))
      : [];

    const bounds = new BoundsAccumulator();
    for (const note of sceneNotes) {
      bounds.add(note.position);
      bounds.add({ x: note.position.x + note.size.width, y: note.position.y + note.size.height });
    }
    for (const node of visibleNodes) {
      for (const corner of boxCorners(node.centre, node.size, node.rotation)) {
        bounds.add(corner);
      }
    }
    for (const block of sceneBlocks) {
      for (const corner of boxCorners(block.centre, block.size, block.collapsed ? block.rotation : 0)) {
        bounds.add(corner);
      }
    }
    for (const edge of sceneEdges) {
      for (const point of edge.path) {
        bounds.add(point);
      }
    }

    for (const run of tubeRuns) {
      bounds.add(run.from);
      bounds.add(run.to);
    }
    const scene: Scene = {
      preview: view.preview ?? null,
      ...(view.units ? { units: view.units } : {}),
      tubeRuns,
      notes: sceneNotes,
      nodes: visibleNodes,
      edges: sceneEdges,
      blocks: sceneBlocks,
      grid: view.grid,
      annotations,
      washed,
      bounds: bounds.result(),
      unresolvedEdges,
      unattachedFindings: index.unattached,
    };
    lastBuild = {
      nodesComputed: nodeMemo.computed,
      edgesComputed: edgeMemo.computed,
      blocksComputed: blockMemo.computed,
    };
    last = { inputs, scene };
    return scene;
  }

  return {
    build,
    get lastBuild() {
      return lastBuild;
    },
  };
}

function sameNote(a: SceneNote, b: SceneNote): boolean {
  return (
    a.text === b.text &&
    a.size.width === b.size.width &&
    a.size.height === b.size.height &&
    a.anchor === b.anchor &&
    a.position.x === b.position.x &&
    a.position.y === b.position.y
  );
}

/** Where on its drawn line a mounted part is drawn, as a fraction of the line. */
export function mountFraction(mount: { distance: number; along?: number | null }, length: number | null): number {
  if (typeof mount.along === "number") return Math.min(1, Math.max(0, mount.along));
  if (length !== null && length > 0) return Math.min(1, Math.max(0, mount.distance / length));
  return 0.5;
}

/**
 * The point `fraction` of the way along a polyline, and how a symbol whose
 * through ports run west to east turns to lie on it there: upstream at the
 * line's start. Leftward, it is flipped rather than turned, so a part with an
 * actuator on top keeps it on top.
 */
export function pointAlong(
  path: readonly Point[],
  fraction: number,
): { point: Point; rotation: number; mirrored: boolean } {
  const lengths = path.slice(1).map((point, index) => Math.hypot(point.x - path[index]!.x, point.y - path[index]!.y));
  const total = lengths.reduce((sum, length) => sum + length, 0);
  if (path.length === 0) return { point: { x: 0, y: 0 }, rotation: 0, mirrored: false };
  let remaining = total * fraction;
  let index = 0;
  while (index < lengths.length - 1 && remaining > lengths[index]!) {
    remaining -= lengths[index]!;
    index += 1;
  }
  const a = path[index]!;
  const b = path[index + 1] ?? a;
  const span = lengths[index] ?? 0;
  const t = span > 0 ? Math.min(1, remaining / span) : 0;
  const point = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  if (Math.abs(dx) >= Math.abs(dy)) return { point, rotation: 0, mirrored: dx < 0 };
  return { point, rotation: dy > 0 ? Math.PI / 2 : -Math.PI / 2, mirrored: false };
}

/** Where along a polyline, as a fraction of it, the point nearest `target` lies. */
export function fractionNearest(path: readonly Point[], target: Point): number {
  const lengths = path.slice(1).map((point, index) => Math.hypot(point.x - path[index]!.x, point.y - path[index]!.y));
  const total = lengths.reduce((sum, length) => sum + length, 0);
  if (total === 0) return 0;
  let best = { distance: Infinity, at: 0 };
  let before = 0;
  lengths.forEach((span, index) => {
    const a = path[index]!;
    const b = path[index + 1]!;
    const t =
      span > 0
        ? Math.min(1, Math.max(0, ((target.x - a.x) * (b.x - a.x) + (target.y - a.y) * (b.y - a.y)) / (span * span)))
        : 0;
    const x = a.x + (b.x - a.x) * t;
    const y = a.y + (b.y - a.y) * t;
    const distance = Math.hypot(target.x - x, target.y - y);
    if (distance < best.distance) best = { distance, at: (before + span * t) / total };
    before += span;
  });
  return best.at;
}

/** One-shot build, for callers that do not keep a builder across edits. */
export function buildScene(
  design: DesignDocument,
  library: SceneLibrary,
  findings: readonly Finding[] = [],
  view: SceneView = DEFAULT_VIEW,
): Scene {
  return createSceneBuilder().build(design, library, findings, view);
}
